import { Badge } from '@monksflow/monks-ui';
import {
  CircleCheckIcon,
  TrendingUpIcon,
  TriangleAlertIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';

import {
  AnalysisMarkdown,
  LabeledContentBlock,
  PriorityPill,
  SectionSeparator,
} from '../../../components';
import { PRIORITY_CONFIG } from '../../../utils/priorityConfig';
import type { BusinessOpportunity } from '../BusinessOpportunities.types';
import { useTranslation } from 'react-i18next';

interface OpportunityPathwayProps {
  opportunity: BusinessOpportunity;
  /** 1-based position of this pathway, e.g. `1`. */
  index: number;
  /** Total number of pathways, e.g. `2`. */
  total: number;
}

/**
 * One Business Opportunity as a self-contained card: a title with its position, the
 * description paired with a tinted priority box, then the impact / feasibility /
 * resources / risks detail blocks stacked below a rule.
 */
export const OpportunityPathway = ({ opportunity, index, total }: OpportunityPathwayProps) => {
  const { t } = useTranslation('analysis');

  const { title, description, priority, timeline, impact, feasibility, resources, risks } =
    opportunity;

  // The priority box takes its tint from the same config the pill reads, so the two
  // always agree on what "high" looks like. In light the text colour comes from there too,
  // to stay paired with the tint; the design's dark tints are deep enough for foreground.
  const priorityConfig = priority
    ? (PRIORITY_CONFIG[priority.toLowerCase()] ?? PRIORITY_CONFIG.low)
    : undefined;

  const details = (
    [
      {
        icon: TrendingUpIcon,
        title: t('analysisResult.opportunityCard.expectedImpact'),
        text: impact,
      },
      {
        icon: CircleCheckIcon,
        title: t('analysisResult.opportunityCard.feasibility'),
        text: feasibility,
      },
      {
        icon: UsersIcon,
        title: t('analysisResult.opportunityCard.resourcesNeeded'),
        text: resources,
      },
      { icon: TriangleAlertIcon, title: t('analysisResult.opportunityCard.keyRisks'), text: risks },
    ] satisfies { icon: LucideIcon; title: string; text?: string }[]
  ).filter((detail) => Boolean(detail.text));

  return (
    <div className="flex flex-col gap-8 rounded-lg border border-border-light p-12">
      <div className="flex flex-col items-start gap-1">
        {title && <h2 className="text-heading-lg font-medium text-foreground">{title}</h2>}
        <Badge variant="secondary" size="sm" roundness="full" className="dark:bg-surface-raised">
          {t('analysisResult.businessOpportunities.growthPathwayIofN', { index, total })}
        </Badge>
      </div>

      <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
        {description && (
          <AnalysisMarkdown text={description} className="text-body-md text-foreground-alt" />
        )}
        {(priority || timeline) && (
          <div className={`flex flex-col gap-3 rounded-lg p-6 ${priorityConfig?.cardBg ?? ''}`}>
            {priority && <PriorityPill priority={priority} />}
            {timeline && (
              <AnalysisMarkdown
                text={timeline}
                className={`text-body-md dark:text-foreground ${priorityConfig?.textColor ?? 'text-foreground'}`}
              />
            )}
          </div>
        )}
      </div>

      {details.length > 0 && (
        <>
          <SectionSeparator emphasis="card" />
          <div className="flex flex-col gap-10">
            {details.map(({ icon, title: detailTitle, text }) => (
              <LabeledContentBlock
                key={detailTitle}
                icon={icon}
                title={detailTitle}
                headingLevel="h3"
                description={text}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
